// Phase 3.5 — Snapshot-bound parts lines for the Pricing Graph.
// Returns actual sold-line samples ONLY when ALL snapshot fields are present.
// Lines missing any snapshot field are surfaced separately as "unresolved".
// No live-config fallback — historical data must be reconstructable from the row itself.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ResolvedActualLine {
  id: string;
  category: string | null;
  cost_each: number;
  quantity: number;
  actual_sell_each: number;
  actual_sell_total: number;
  pricing_method: string;
  pricing_source: string;
  manual_price_locked: boolean;
  matrix_multiplier_snapshot: number;
  matrix_quantity_mode_snapshot: 'per_unit' | 'grouped';
  is_canned: boolean;
  created_at: string;
}

export interface PartsLinesForGraphResult {
  resolved: ResolvedActualLine[];
  unresolvedCount: number;
}

export interface UsePartsLinesForGraphArgs {
  shopId: string | undefined;
  from?: Date | null;
  to?: Date | null;
  category?: string | null;
  excludeCanned?: boolean;
  overridesOnly?: boolean;
  limit?: number;
}

export function usePartsLinesForGraph(args: UsePartsLinesForGraphArgs) {
  const {
    shopId, from, to, category,
    excludeCanned = true, overridesOnly = false, limit = 500,
  } = args;

  return useQuery({
    queryKey: [
      'parts-lines-for-graph', shopId,
      from?.toISOString() ?? null, to?.toISOString() ?? null,
      category ?? null, excludeCanned, overridesOnly, limit,
    ] as const,
    enabled: !!shopId,
    queryFn: async (): Promise<PartsLinesForGraphResult> => {
      // Pull lines + parent job (for canned detection via catalog_job_id) + ticket (for shop scoping).
      let q = (supabase as any)
        .from('parts_lines')
        .select(`
          id, category, quantity,
          cost_each_snapshot, quantity_snapshot,
          actual_sell_each, actual_sell_total,
          pricing_method, pricing_source, manual_price_locked,
          matrix_multiplier_snapshot, matrix_quantity_mode_snapshot,
          created_at,
          jobs!inner(catalog_job_id, ticket_id, tickets!inner(shop_id))
        `)
        .eq('jobs.tickets.shop_id', shopId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (from) q = q.gte('created_at', from.toISOString());
      if (to) q = q.lte('created_at', to.toISOString());
      if (category) q = q.eq('category', category);
      if (overridesOnly) q = q.eq('manual_price_locked', true);

      const { data, error } = await q;
      if (error) throw error;

      const rows = (data ?? []) as any[];

      const resolved: ResolvedActualLine[] = [];
      let unresolvedCount = 0;

      for (const r of rows) {
        const isCanned = !!r.jobs?.catalog_job_id;
        if (excludeCanned && isCanned) continue;

        const mode = r.matrix_quantity_mode_snapshot as 'per_unit' | 'grouped' | null;
        const cost = r.cost_each_snapshot != null ? Number(r.cost_each_snapshot) : null;
        const qty = r.quantity_snapshot != null ? Number(r.quantity_snapshot) : null;
        const mult = r.matrix_multiplier_snapshot != null ? Number(r.matrix_multiplier_snapshot) : null;

        // Authoritative actual depends on mode.
        let actualEach: number | null = null;
        let actualTotal: number | null = null;
        if (mode === 'grouped') {
          actualTotal = r.actual_sell_total != null ? Number(r.actual_sell_total) : null;
          actualEach = actualTotal != null && qty && qty > 0
            ? Math.round((actualTotal / qty) * 100) / 100
            : null;
        } else if (mode === 'per_unit') {
          actualEach = r.actual_sell_each != null ? Number(r.actual_sell_each) : null;
          actualTotal = actualEach != null && qty != null
            ? Math.round(actualEach * qty * 100) / 100
            : null;
        }

        // Strict snapshot-bound: ALL of cost, qty, mult, mode, and a resolved actual must be present.
        const fullySnapshotted =
          cost != null && qty != null && mult != null && mode != null &&
          actualEach != null && actualTotal != null;

        if (!fullySnapshotted) {
          unresolvedCount += 1;
          continue;
        }

        resolved.push({
          id: r.id,
          category: r.category,
          cost_each: cost!,
          quantity: qty!,
          actual_sell_each: actualEach!,
          actual_sell_total: actualTotal!,
          pricing_method: r.pricing_method ?? 'manual_entry',
          pricing_source: r.pricing_source ?? 'manual',
          manual_price_locked: !!r.manual_price_locked,
          matrix_multiplier_snapshot: mult!,
          matrix_quantity_mode_snapshot: mode!,
          is_canned: isCanned,
          created_at: r.created_at,
        });
      }

      return { resolved, unresolvedCount };
    },
  });
}
